import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { API } from '../config/api';
import UserAvatar from '../components/UserAvatar';
import {
  Radio, Inbox, Filter, CheckCircle, XCircle, Eye, Clock,
  Music, Video, User, Mic, Camera, Palette, Headphones, Loader2,
  RefreshCw, ChevronDown
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const STATUS_CONFIG = {
  new: { label: 'New', color: 'bg-blue-500', icon: Radio },
  seen: { label: 'Seen', color: 'bg-gray-500', icon: Eye },
  accepted: { label: 'Accepted', color: 'bg-green-500', icon: CheckCircle },
  declined: { label: 'Declined', color: 'bg-red-500', icon: XCircle },
  fulfilled: { label: 'Fulfilled', color: 'bg-purple-500', icon: CheckCircle },
};

const TYPE_ICONS = {
  song: Music,
  video: Video,
  creator: User,
  podcast: Mic,
  stream: Camera,
  service: Palette,
};

export default function RequestsInboxPage() {
  const { getAuthHeaders, isAuthenticated } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [newCount, setNewCount] = useState(0);
  const [total, setTotal] = useState(0);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const filterParam = filter === 'all' ? '' : `?status=${filter}`;
      const res = await fetch(`${API}/requests/inbox${filterParam}`, {
        headers: getAuthHeaders()
      });
      
      if (res.ok) {
        const data = await res.json();
        setRequests(data.requests || []);
        setNewCount(data.new_count || 0);
        setTotal(data.total || 0);
      } else {
        toast.error('Failed to load requests');
      }
    } catch (err) {
      console.error('Error fetching requests:', err);
      toast.error('Failed to load requests');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchRequests();
    }
  }, [isAuthenticated, filter]);

  const updateStatus = async (requestId, newStatus) => {
    try {
      const res = await fetch(`${API}/requests/${requestId}/status`, {
        method: 'PATCH',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });

      if (res.ok) {
        setRequests(prev => prev.map(r => 
          r.request_id === requestId ? { ...r, status: newStatus } : r
        ));
        if (newStatus === 'seen') {
          setNewCount(prev => Math.max(0, prev - 1));
        }
        toast.success(`Request marked as ${newStatus}`);
      }
    } catch (err) {
      toast.error('Failed to update request');
    }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diff = Math.floor((now - date) / 1000);
    
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  if (!isAuthenticated) {
    return (
      <div className="container max-w-4xl mx-auto py-8 px-4">
        <Card className="p-8 text-center">
          <Radio className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
          <h2 className="text-xl font-bold mb-2">Sign in Required</h2>
          <p className="text-muted-foreground">Please sign in to view your requests inbox.</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="container max-w-4xl mx-auto py-8 px-4" data-testid="requests-inbox-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-primary/10 rounded-xl">
            <Inbox className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              Requests Inbox
              {newCount > 0 && (
                <Badge variant="destructive" className="animate-pulse">
                  {newCount} new
                </Badge>
              )}
            </h1>
            <p className="text-muted-foreground text-sm">
              {total} total request{total !== 1 ? 's' : ''}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Filter Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="gap-2">
                <Filter className="w-4 h-4" />
                {filter === 'all' ? 'All' : STATUS_CONFIG[filter]?.label}
                <ChevronDown className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem onClick={() => setFilter('all')}>
                All Requests
              </DropdownMenuItem>
              {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                <DropdownMenuItem key={key} onClick={() => setFilter(key)}>
                  <config.icon className="w-4 h-4 mr-2" />
                  {config.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Refresh */}
          <Button variant="ghost" size="icon" onClick={fetchRequests} disabled={loading}>
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Requests List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      ) : requests.length === 0 ? (
        <Card className="p-12 text-center">
          <Radio className="w-16 h-16 mx-auto text-muted-foreground mb-4 opacity-50" />
          <h2 className="text-xl font-semibold mb-2">No requests yet</h2>
          <p className="text-muted-foreground">
            When listeners request your content, it will appear here.
          </p>
        </Card>
      ) : (
        <div className="space-y-4">
          {requests.map((request) => {
            const TypeIcon = TYPE_ICONS[request.target_type] || Radio;
            const statusConfig = STATUS_CONFIG[request.status] || STATUS_CONFIG.new;
            const StatusIcon = statusConfig.icon;

            return (
              <Card 
                key={request.request_id} 
                className={`p-4 transition-all ${request.status === 'new' ? 'border-primary/50 bg-primary/5' : ''}`}
                data-testid={`request-${request.request_id}`}
              >
                <div className="flex items-start gap-4">
                  {/* Requester Avatar */}
                  <UserAvatar
                    src={request.requester_avatar}
                    name={request.requester_name}
                    size="md"
                  />

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="font-semibold">{request.requester_name}</span>
                      <Badge variant="outline" className="text-xs">
                        {request.requester_role}
                      </Badge>
                      <span className="text-muted-foreground text-sm">
                        requested your
                      </span>
                      <Badge variant="secondary" className="gap-1">
                        <TypeIcon className="w-3 h-3" />
                        {request.target_type}
                      </Badge>
                    </div>

                    {/* Target */}
                    <p className="text-sm text-muted-foreground mb-2">
                      <span className="font-medium text-foreground">{request.target_name}</span>
                    </p>

                    {/* Message */}
                    {request.message && (
                      <div className="bg-muted/50 rounded-lg p-3 mb-2">
                        <p className="text-sm">{request.message}</p>
                      </div>
                    )}

                    {/* Dedication/Reason */}
                    {(request.dedication || request.reason) && (
                      <div className="flex flex-wrap gap-2 mb-2">
                        {request.dedication && (
                          <Badge variant="outline" className="text-xs">
                            Dedicated to: {request.dedication}
                          </Badge>
                        )}
                        {request.format_preference && (
                          <Badge variant="outline" className="text-xs">
                            Format: {request.format_preference}
                          </Badge>
                        )}
                      </div>
                    )}

                    {/* Time & Status */}
                    <div className="flex items-center gap-3 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {formatTime(request.created_at)}
                      </span>
                      <Badge 
                        className={`${statusConfig.color} text-foreground text-xs gap-1`}
                      >
                        <StatusIcon className="w-3 h-3" />
                        {statusConfig.label}
                      </Badge>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col gap-2">
                    {request.status === 'new' && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => updateStatus(request.request_id, 'seen')}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        Mark Seen
                      </Button>
                    )}
                    {(request.status === 'new' || request.status === 'seen') && (
                      <>
                        <Button
                          size="sm"
                          variant="default"
                          className="bg-green-600 hover:bg-green-700"
                          onClick={() => updateStatus(request.request_id, 'accepted')}
                        >
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Accept
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-500 border-red-500/50 hover:bg-red-500/10"
                          onClick={() => updateStatus(request.request_id, 'declined')}
                        >
                          <XCircle className="w-4 h-4 mr-1" />
                          Decline
                        </Button>
                      </>
                    )}
                    {request.status === 'accepted' && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-purple-500 border-purple-500/50"
                        onClick={() => updateStatus(request.request_id, 'fulfilled')}
                      >
                        <CheckCircle className="w-4 h-4 mr-1" />
                        Mark Fulfilled
                      </Button>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
